import React from 'react';
import { Link } from 'react-router-dom';
import type {
  AgentActionItem, AgentDashboardResponse, AgentFarmArea, AgentPerformance,
} from 'types/agent-dashboard';

import './AgentHomePage.css';

import {
  ButtonBase, CircularProgress, ClickAwayListener, Tooltip,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ArrowForward from '@material-ui/icons/ArrowForward';
import CameraAlt from '@material-ui/icons/CameraAlt';
import Check from '@material-ui/icons/Check';
import EventOutlined from '@material-ui/icons/EventOutlined';
import ExpandMore from '@material-ui/icons/ExpandMore';
import MailOutline from '@material-ui/icons/MailOutline';
import PeopleOutline from '@material-ui/icons/PeopleOutline';
import PhoneOutlined from '@material-ui/icons/PhoneOutlined';
import QuestionAnswerOutlined from '@material-ui/icons/QuestionAnswerOutlined';
import TrendingUp from '@material-ui/icons/TrendingUp';

import ActionModal from 'components/ActionModal/ActionModal';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { fetchAgentDashboard, updateAgentActionItem } from 'utils/api/agent-dashboard';

import AgentDashboardTour from './AgentDashboardTour';
import AgentOnboarding from './AgentOnboarding';

const number = (value: number | null | undefined) => (value == null ? '—' : value.toLocaleString('en-US', { maximumFractionDigits: 1 }));
const money = (value: number | null | undefined, compact = false) => (value == null
  ? '—'
  : new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: compact ? 2 : 0,
    notation: compact ? 'compact' : 'standard',
  }).format(value));
const date = (value?: string, full = false) => (value && !Number.isNaN(Date.parse(value))
  ? new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(full ? ({ year: 'numeric', hour: 'numeric', minute: '2-digit' } as const) : {}),
  })
  : 'Date unavailable');
const mailingDate = (value: string) => {
  const scheduled = new Date(value);
  const day = scheduled.getDate();
  const suffix = day >= 11 && day <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] || 'th';
  return `${scheduled.toLocaleDateString('en-US', { month: 'short' })} ${day}${suffix}`;
};
const initials = (name: string) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();
// QR scans share repeat_visit with ordinary visits. The API identifies them in
// generated titles and activity labels; retain the title check for capped histories.
const isQrAction = (item: AgentActionItem) => (
  (item.kind === 'repeat_visit' && /\bQR code scans?\b/i.test(item.title))
  || item.activity.some((entry) => entry.label === 'QR code scanned')
);
const signalLabel = (item: AgentActionItem) => {
  if (isQrAction(item)) return null;
  if (item.status === 'done') return 'Follow-up completed';
  if (item.kind === 'inquiry') return 'Start with a real request';
  if (item.kind === 'reply') return 'A reply worth a conversation';
  return null;
};
const personAddress = (item: AgentActionItem) => item.person.siteAddress || item.person.mailingAddress
  || [item.person.address, item.person.zipCode].filter(Boolean).join(', ');
const actionHeading = (value: string) => value.trim()
  .replace(/\.+$/, '')
  .replace(/ scanned your QR code once$/, ' scanned your QR code')
  .replace(/\bover 1 day\b/g, 'over a day');
const commissionRate = 0.03;
const touchLabels = {
  EMAIL: 'Email', POSTCARD: 'Postcard', LETTER: 'Letter', VOICEMAIL: 'Voicemail', TEXT: 'Text message',
};

const getOpportunity = (area?: AgentFarmArea) => {
  const metrics = area?.metrics;
  if (!metrics || metrics.numberOfHomes == null || metrics.averageSalesPrice == null || metrics.turnoverRate == null) {
    return null;
  }
  return metrics.numberOfHomes * (metrics.turnoverRate / 100) * metrics.averageSalesPrice * commissionRate;
};

const metricDescriptions = {
  brandedTouches: 'Recorded email, mail, voicemail, and text sends across your entire recorded campaign history; the smaller number shows the last 90 days. These are sends, not verified views or unique people reached.',
  engagedProspects: 'Distinct known prospects with an email reply, link click, QR code scan, landing page visit, form submission, or report request across your entire recorded campaign history.',
  qrLinkedVisits: 'QR code scans from your letters and postcards across your recorded history.',
  inquiries: 'Distinct prospects submitting a form or requesting a report across your entire recorded campaign history; the smaller number shows the last 90 days. These are not appointments, listings, or closed transactions.',
};

const MetricInfo = ({ label, description }: { label: string; description: string }) => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', dismiss);
    return () => document.removeEventListener('keydown', dismiss);
  }, [open]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <span className="ah-info-wrap">
        <Tooltip
          title={description}
          arrow
          interactive
          placement="top"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disableTouchListener
          enterDelay={0}
          TransitionProps={{ timeout: 0 }}
          leaveDelay={150}
          classes={{ tooltip: 'ah-stat-tooltip' }}
        >
          <button type="button" className="ah-info" aria-label={label} onClick={() => setOpen(true)}>
            <span aria-hidden="true">i</span>
          </button>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};

const RecentValue = ({ value }: { value: number }) => (
  <span className="ah-recent-value">
    <strong>{number(value)}</strong>
    {' '}
    last 90 days
  </span>
);

const tabKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
  if (current < 0) return;
  const next = event.key === 'Home'
    ? 0
    : event.key === 'End'
      ? tabs.length - 1
      : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
  event.preventDefault();
  tabs[next].focus();
  tabs[next].click();
};

const EngagementChart = ({ performance }: { performance: AgentPerformance['last90Days'] }) => {
  const dailyValues = performance.dailyEngagements;
  // Match the reference's 30-bar density while retaining the full reporting period.
  const daysPerBar = Math.max(1, Math.ceil(dailyValues.length / 30));
  const values = Array.from({ length: Math.ceil(dailyValues.length / daysPerBar) }, (_, index) => {
    const days = dailyValues.slice(index * daysPerBar, (index + 1) * daysPerBar);
    return {
      date: days[0].date,
      endDate: days[days.length - 1].date,
      count: days.reduce((total, day) => total + day.count, 0),
    };
  });
  const max = Math.max(2, ...values.map((entry) => entry.count));
  const step = 464 / Math.max(1, values.length);
  return (
    <>
      <svg className="ah-chart" viewBox="0 0 500 155" role="img" aria-labelledby="ah-chart-description">
        <title id="ah-chart-description">
          {`Recorded engagement events in UTC, ${daysPerBar === 1 ? 'daily' : `grouped into ${daysPerBar}-day totals`}. ${number(
            performance.engagementEvents,
          )} events in this period; a prospect can engage more than once.`}
        </title>
        {[0, 1, 2].map((tick) => (
          <g key={tick}>
            <line className="ah-gridline" x1="29" x2="499" y1={17 + tick * 48} y2={17 + tick * 48} />
            <text x="0" y={21 + tick * 48}>
              {number(max * (1 - tick / 2))}
            </text>
          </g>
        ))}
        {values.map((entry, index) => (
          <rect
            key={entry.date}
            x={32 + index * step}
            y={113 - (entry.count / max) * 96}
            width={Math.max(2, step - 4)}
            height={(entry.count / max) * 96}
            rx="2.5"
            className={index >= values.length - 3 ? 'ah-chart-bar ah-chart-bar-recent' : 'ah-chart-bar'}
          >
            <title>
              {entry.date}
              {entry.endDate !== entry.date ? ` – ${entry.endDate}` : ''}
              :
              {number(entry.count)}
              {' '}
              engagement events
            </title>
          </rect>
        ))}
        {[0, Math.floor((dailyValues.length - 1) / 2), dailyValues.length - 1]
          .filter((index, position, all) => index >= 0 && all.indexOf(index) === position)
          .map((index, position) => (
            <text
              key={index}
              x={position === 0 ? 29 : position === 1 ? 250 : 499}
              y="145"
              textAnchor={position === 0 ? 'start' : position === 1 ? 'middle' : 'end'}
            >
              {new Date(`${dailyValues[index].date}T12:00:00Z`).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </text>
          ))}
      </svg>
      <div className="ah-chart-caption">
        <span>
          <i />
          {' '}
          Engagement events
        </span>
        {daysPerBar > 1 && <span>{`${daysPerBar}-day totals`}</span>}
      </div>
    </>
  );
};

const AgentHomePage = () => {
  const theme = useTheme();
  const { userInfo } = useUserInfoContext();
  const [refresh, setRefresh] = React.useState(0);
  const [data, setData] = React.useState<AgentDashboardResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [tab, setTab] = React.useState<AgentActionItem['status']>('todo');
  const [zip, setZip] = React.useState('');
  const [expandedAction, setExpandedAction] = React.useState<string | null>(null);
  const [selectedAction, setSelectedAction] = React.useState<string | null>(null);
  const [dialog, setDialog] = React.useState<'activity' | 'calculation' | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const [refreshingMarket, setRefreshingMarket] = React.useState(false);
  const [marketRefreshError, setMarketRefreshError] = React.useState(false);
  const [marketRefreshMessage, setMarketRefreshMessage] = React.useState('');
  const [marketRefreshRequest, setMarketRefreshRequest] = React.useState<{ zipCode: string; fetchedAt?: string | null; status: string } | null>(null);

  React.useEffect(() => {
    if (!marketRefreshRequest) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let checks = 0;
    const finish = (message: string, failed = false) => {
      setMarketRefreshMessage(message);
      setMarketRefreshError(failed);
      setRefreshingMarket(false);
    };
    const checkMarket = async () => {
      try {
        const response = await fetchAgentDashboard(controller.signal);
        if (cancelled) return;
        // Market polling must never undo follow-ups completed during the refresh.
        setData((current) => (current ? { ...current, areas: response.areas } : current));
        const updated = response.areas.find((entry) => entry.zipCode === marketRefreshRequest.zipCode);
        if (!updated) {
          finish('Market data for this ZIP is no longer available.', true);
        } else if (updated.status === 'ready') {
          finish('Your market data is up to date.');
        } else if (updated.fetchedAt && updated.fetchedAt !== marketRefreshRequest.fetchedAt) {
          finish('Market data updated. Some figures are still unavailable.');
        } else if (response.marketRefreshStatus === 'unavailable') {
          finish('We couldn’t start the market refresh. Please try again.', true);
        } else if (updated.status === 'unavailable' && marketRefreshRequest.status === 'pending') {
          finish('Market data is still unavailable. Please try again later.', true);
        } else if (++checks >= 12) {
          finish('This is taking longer than expected. You can keep using your dashboard and check again shortly.');
        } else {
          setMarketRefreshMessage(response.marketRefreshStatus === 'queued'
            ? 'Your market refresh is in progress. This card will update automatically when data is available.'
            : 'Waiting for updated market data. This card will update automatically when data is available.');
          timer = setTimeout(checkMarket, 10000);
        }
      } catch {
        if (!cancelled) finish('We couldn’t check for an update. Please try again.', true);
      }
    };
    checkMarket();
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [marketRefreshRequest]);

  React.useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchAgentDashboard(controller.signal)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [refresh]);

  const items = data?.actionItems ?? [];
  const shown = items.filter((item) => item.status === tab)
    .sort((a, b) => Date.parse(b.lastActivityAt) - Date.parse(a.lastActivityAt));
  const expandedId = (shown.find((item) => item._id === expandedAction) ?? shown[0])?._id;
  React.useEffect(() => { setExpandedAction(null); }, [tab]);
  const action = items.find((item) => item._id === selectedAction);
  const siteAddress = action?.person.siteAddress;
  const area = data?.areas.find((item) => item.zipCode === zip) ?? data?.areas[0];
  const opportunity = getOpportunity(area);
  const performance = data?.performance;
  const lifetime = performance?.lifetime;
  const recent = performance?.last90Days;
  const noTouches = lifetime?.brandedTouches === 0;
  const campaignLaunch = data?.campaignLaunch;
  const hasLaunchedCampaign = !!campaignLaunch && ['PENDING_DATA', 'PENDING_BILLING', 'ACTIVE'].includes(campaignLaunch.status);
  const gettingStarted = noTouches && hasLaunchedCampaign;
  const emptyActionTitle = noTouches ? 'Your next conversations start here' : 'You’re all caught up.';
  const emptyActionDescription = noTouches ? 'Your follow-ups will appear here as prospects engage.' : 'The next useful signal will find you here as new activity is processed.';
  const openActivity = (item: AgentActionItem) => {
    setSelectedAction(item._id);
    setSaveError(false);
    setDialog('activity');
  };
  const closeDialog = () => {
    if (!saving) setDialog(null);
  };
  const refreshMarket = () => {
    if (!area || refreshingMarket) return;
    setRefreshingMarket(true);
    setMarketRefreshError(false);
    setMarketRefreshMessage('Requesting updated market data…');
    setMarketRefreshRequest({ zipCode: area.zipCode, fetchedAt: area.fetchedAt, status: area.status });
  };
  const saveAction = async () => {
    if (!action || saving) return;
    setSaving(true);
    setSaveError(false);
    try {
      const updated = await updateAgentActionItem(action._id, action.status === 'todo' ? 'done' : 'todo');
      setData((current) => (current
        ? { ...current, actionItems: current.actionItems.map((item) => (item._id === updated._id ? updated : item)) }
        : current));
      setNotice(updated.status === 'done' ? 'Follow-up marked done.' : 'Follow-up reopened.');
      setDialog(null);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = userInfo?.firstName?.trim().split(/\s+/)[0];
  const channelCount = (types: string[], period: 'lifetime' | 'last90Days' = 'lifetime') => performance?.[period].channels
    .filter((entry) => types.includes(entry.type))
    .reduce((total, entry) => total + entry.count, 0) ?? 0;

  if (!loading && !error && data?.onboarding && noTouches && lifetime?.engagementEvents === 0 && !items.length && !campaignLaunch) {
    return (
      <main className="agent-home">
        <AgentOnboarding firstName={firstName} drafts={data.onboarding.drafts} />
      </main>
    );
  }

  return (
    <main className="agent-home">
      {!loading && !error && data && (
        <header className="ah-heading">
          <div>
            <h1>
              {greeting}
              {firstName ? `, ${firstName}` : ''}
            </h1>
            <p>A little focus. A few good conversations. Your next move starts here.</p>
            <AgentDashboardTour />
          </div>
          {hasLaunchedCampaign && (
            <Link className="ah-button ah-primary ah-heading-campaigns" to="/dashboard/campaigns">
              Go to campaigns
              <ArrowForward />
            </Link>
          )}
        </header>
      )}
      <div className="ah-sr-only" role="status" aria-live="polite">
        {notice}
      </div>
      {loading ? (
        <div className="ah-loading" role="status">
          <CircularProgress size={28} aria-label="Loading dashboard" />
        </div>
      ) : error || !lifetime || !recent ? (
        <div className="ah-empty" role="alert">
          <h2>Your dashboard couldn’t load.</h2>
          <p>Please try again to see your latest activity.</p>
          <button type="button" className="ah-button ah-primary" onClick={() => setRefresh((value) => value + 1)}>
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="ah-focus-grid">
            <section aria-labelledby="ah-actions-title">
              <div className="ah-section-head">
                <h2 id="ah-actions-title" data-tour-id="dashboard-actions">
                  Your action items
                  <span className="ah-total">{items.filter((item) => item.status === 'todo').length}</span>
                </h2>
                <div
                  className="ah-tabs"
                  role="tablist"
                  tabIndex={-1}
                  aria-label="Follow-up status"
                  onKeyDown={tabKeyDown}
                >
                  {(['todo', 'done'] as const).map((status) => (
                    <button
                      type="button"
                      key={status}
                      id={`ah-${status}-tab`}
                      role="tab"
                      aria-selected={tab === status}
                      aria-controls="ah-action-list"
                      tabIndex={tab === status ? 0 : -1}
                      onClick={() => setTab(status)}
                    >
                      {status === 'todo' ? 'To do' : 'Done'}
                      {' '}
                      <span>{items.filter((item) => item.status === status).length}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div id="ah-action-list" role="tabpanel" aria-labelledby={`ah-${tab}-tab`} aria-live="polite">
                {shown.length ? (
                  <div className="ah-action-accordion">
                    {shown.map((item) => {
                      const expanded = item._id === expandedId;
                      const qrAction = isQrAction(item);
                      return (
                        <article className={expanded ? 'ah-focus-action' : 'ah-action-collapsed'} data-kind={item.kind} data-status={item.status} data-signal={qrAction ? 'qr' : undefined} key={item._id}>
                          {expanded ? (
                            <div className={`ah-focus-top${signalLabel(item) ? '' : ' ah-focus-top-compact'}`}>
                              {signalLabel(item) && (
                                <span className="ah-pill">
                                  <i />
                                  {signalLabel(item)}
                                </span>
                              )}
                              <span className="ah-action-date">
                                <time dateTime={item.lastActivityAt} title={date(item.lastActivityAt, true)}>{date(item.lastActivityAt)}</time>
                              </span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="ah-signal ah-action-toggle"
                              aria-label={`Expand ${item.person.name}’s follow-up`}
                              aria-expanded={false}
                              aria-controls={`ah-action-details-${item._id}`}
                              onClick={() => setExpandedAction(item._id)}
                            >
                              <span className="ah-signal-icon">
                                {qrAction || item.kind === 'repeat_visit' ? <CameraAlt /> : item.kind === 'email_open' || item.kind === 'reply' ? <MailOutline /> : <TrendingUp />}
                              </span>
                              <span className="ah-signal-copy">
                                <strong>
                                  {item.person.name}
                                  <time dateTime={item.lastActivityAt}>{date(item.lastActivityAt)}</time>
                                </strong>
                                <span className="ah-signal-description">{actionHeading(item.description)}</span>
                              </span>
                              <ExpandMore />
                            </button>
                          )}
                          <div
                            id={`ah-action-details-${item._id}`}
                            className="ah-action-details"
                            role="region"
                            aria-labelledby={`ah-action-title-${item._id}`}
                            hidden={!expanded}
                          >
                            <h3 id={`ah-action-title-${item._id}`}>{actionHeading(item.title)}</h3>
                            <p>{actionHeading(item.description)}</p>
                            <div className="ah-person">
                              <div className="ah-avatar">{initials(item.person.name)}</div>
                              <div>
                                <strong>{item.person.name}</strong>
                                <span>{personAddress(item)}</span>
                              </div>
                            </div>
                            <div className="ah-focus-buttons">
                              {item.campaign?.name && (
                                <div className="ah-card-campaign">
                                  Campaign:
                                  {' '}
                                  <Link className="ah-campaign-link" to={`/dashboard/campaigns/${item.campaign.id}`} target="_blank" rel="noopener noreferrer">
                                    {item.campaign.name}
                                  </Link>
                                </div>
                              )}
                              <button type="button" className="ah-button ah-primary" onClick={() => openActivity(item)}>
                                {tab === 'done' ? <Check /> : qrAction ? <CameraAlt /> : <MailOutline />}
                                {tab === 'done' ? 'View follow-up' : 'Review follow-up'}
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ah-empty">
                    <Check />
                    <h3>{tab === 'todo' ? emptyActionTitle : 'A fresh start.'}</h3>
                    <p>
                      {tab === 'todo'
                        ? emptyActionDescription
                        : 'Your completed follow-ups will appear here.'}
                    </p>
                  </div>
                )}
              </div>
            </section>
            <section className="ah-brand-panel" aria-labelledby="ah-brand-title">
              {noTouches ? (
                <div className="ah-brand-start">
                  <div className="ah-eyebrow">Your brand, in motion</div>
                  <h2 id="ah-brand-title">{gettingStarted ? 'Your campaign is getting started' : 'Start your next conversations'}</h2>
                  <p>{gettingStarted ? 'Your outreach results will appear here as your first touches go out.' : 'Launch a campaign to start reaching prospects and see your outreach results here.'}</p>
                  {gettingStarted && campaignLaunch?.nextSendAt && (
                    <div className="ah-first-send">
                      <span>First scheduled send</span>
                      <strong>{date(campaignLaunch.nextSendAt, true)}</strong>
                    </div>
                  )}
                  <Link className="ah-button ah-primary" to={gettingStarted && campaignLaunch ? `/dashboard/campaigns/${campaignLaunch.id}` : '/dashboard/create-campaign'}>
                    {gettingStarted ? 'View your campaign' : 'Launch a campaign'}
                    <ArrowForward />
                  </Link>
                </div>
              ) : (
                <>
                  <div className="ah-brand-intro">
                    <div className="ah-eyebrow">Your brand, in motion</div>
                    <h2 id="ah-brand-title">More chances to be the agent they remember.</h2>
                    <div className="ah-brand-count">{number(lifetime.brandedTouches)}</div>
                    <div className="ah-brand-caption">
                      branded touches sent
                      <MetricInfo label="About branded touches sent" description={metricDescriptions.brandedTouches} />
                    </div>
                    <RecentValue value={recent.brandedTouches} />
                  </div>
                  <div className="ah-brand-summary">
                    <strong>Your name. Your face. Your expertise.</strong>
                    <p>
                      {number(channelCount(['EMAIL', 'POSTCARD', 'LETTER']))}
                      {' '}
                      branded emails and mail pieces, plus
                      {' '}
                      {number(channelCount(['VOICEMAIL']))}
                      {' '}
                      voicemails, helping you stay familiar.
                    </p>
                  </div>
                  <div className="ah-metrics">
                    {[
                      {
                        label: 'Prospects engaged',
                        tone: 'green',
                        icon: <PeopleOutline />,
                        caption: 'Replies, clicks, visits & requests',
                        value: lifetime.engagedProspects,
                        recentValue: recent.engagedProspects,
                        description: metricDescriptions.engagedProspects,
                      },
                      {
                        label: 'QR code scans',
                        tone: 'neutral',
                        icon: <CameraAlt />,
                        caption: 'A closer look at your materials',
                        value: lifetime.qrLinkedVisits,
                        recentValue: recent.qrLinkedVisits,
                        description: metricDescriptions.qrLinkedVisits,
                      },
                      {
                        label: 'Inquiries',
                        tone: 'blue',
                        icon: <QuestionAnswerOutlined />,
                        hidden: lifetime.inquiries === 0,
                        caption: 'Conversations ready to start',
                        value: lifetime.inquiries,
                        recentValue: recent.inquiries,
                        description: metricDescriptions.inquiries,
                      },
                    ].filter((metric) => !metric.hidden).map(({
                      label, caption, value, recentValue, description, tone, icon,
                    }) => (
                      <article className="ah-metric" data-tone={tone} key={label}>
                        <div>
                          <div className="ah-metric-label">
                            <span className="ah-metric-icon" aria-hidden="true">{icon}</span>
                            <span>{label}</span>
                            <MetricInfo label={`About ${label.toLowerCase()}`} description={description} />
                          </div>
                          <p>{caption}</p>
                        </div>
                        <div className="ah-metric-values">
                          <strong>{number(value)}</strong>
                          <RecentValue value={recentValue} />
                        </div>
                      </article>
                    ))}
                  </div>
                  <p className="ah-brand-note">
                    <TrendingUp />
                    Consistency keeps your brand familiar.
                  </p>
                </>
              )}
            </section>
          </div>
          <section className="ah-areas" aria-labelledby="ah-farm-title">
            <div className="ah-section-head">
              <h2 id="ah-farm-title" data-tour-id="dashboard-farm">There’s opportunity in your farm area.</h2>
              {!!data?.areas.length && (
                <div
                  className="ah-area-tabs"
                  role="tablist"
                  tabIndex={-1}
                  aria-label="Farm ZIP codes"
                  onKeyDown={tabKeyDown}
                >
                  {data?.areas.map((item) => (
                    <button
                      type="button"
                      key={item.zipCode}
                      id={`ah-area-${item.zipCode}`}
                      role="tab"
                      aria-selected={area?.zipCode === item.zipCode}
                      aria-controls="ah-area-detail"
                      tabIndex={area?.zipCode === item.zipCode ? 0 : -1}
                      onClick={() => setZip(item.zipCode)}
                    >
                      {item.zipCode}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {area ? (
              <div
                className="ah-panel ah-area-panel"
                id="ah-area-detail"
                role="tabpanel"
                aria-labelledby={`ah-area-${area.zipCode}`}
              >
                <div className="ah-opportunity">
                  <h3 className="ah-opportunity-heading">
                    <span className="ah-opportunity-label">Annual commission opportunity</span>
                    {' '}
                    <span className="ah-opportunity-zip">
                      in ZIP
                      {' '}
                      {area.zipCode}
                    </span>
                  </h3>
                  <div className="ah-opportunity-value">{money(opportunity, true)}</div>
                  {opportunity == null ? (
                    <p>The estimate will appear when verified market data is available for this ZIP.</p>
                  ) : (
                    <div className="ah-market-insight ah-commission-insight">
                      <TrendingUp />
                      <div>
                        <h3>Estimated annual commission pool in this ZIP, assuming a 3% commission rate.</h3>
                        <button type="button" className="ah-text-button" onClick={() => setDialog('calculation')}>
                          See the math
                          <ArrowForward />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="ah-area-data">
                  <div className="ah-area-detail-head">
                    <span>
                      {number(area.farmedProperties)}
                      {' '}
                      properties in your farm in ZIP
                      {' '}
                      {area.zipCode}
                    </span>
                  </div>
                  <dl className="ah-area-stats">
                    {[
                      ['Properties in this ZIP', number(area.metrics?.numberOfHomes)],
                      ['Average sale price', money(area.metrics?.averageSalesPrice, true)],
                      [
                        'Annual turnover',
                        area.metrics?.turnoverRate == null ? '—' : `${number(area.metrics.turnoverRate)}%`,
                      ],
                      [
                        'Non-owner ratio',
                        area.metrics?.nonOwnerRatio == null ? '—' : `${number(area.metrics.nonOwnerRatio)}%`,
                      ],
                      [
                        'Average years owned',
                        area.metrics?.averageYearsOwned == null
                          ? '—'
                          : `${number(area.metrics.averageYearsOwned)} years`,
                      ],
                      [
                        'Est. sales per year',
                        area.metrics?.numberOfHomes == null || area.metrics?.turnoverRate == null
                          ? '—'
                          : `~${Math.round(
                            area.metrics.numberOfHomes * (area.metrics.turnoverRate / 100),
                          ).toLocaleString()}`,
                      ],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                  {(area.status !== 'ready' || opportunity == null) && (
                    <div className="ah-market-insight">
                      <TrendingUp />
                      <div>
                        <h3>
                          {area.status === 'pending'
                            ? 'Your local market data is being prepared.'
                            : area.status === 'stale'
                              ? 'Showing the last available market data.'
                              : area.status === 'partial'
                                ? 'Some local market figures are unavailable.'
                                : 'Some market figures are unavailable.'}
                        </h3>
                        {(area.note || area.fetchedAt) && (
                          <p>
                            {area.note}
                            {area.fetchedAt && (
                              <>
                                {' '}
                                {`Retrieved ${date(area.fetchedAt)}.`}
                              </>
                            )}
                          </p>
                        )}
                        {(['pending', 'stale'].includes(area.status) || (['unavailable', 'partial'].includes(area.status) && !!area.countyStats?.length)) && (
                          <button
                            type="button"
                            className="ah-text-button"
                            disabled={refreshingMarket}
                            onClick={refreshMarket}
                          >
                            {refreshingMarket && <CircularProgress size={16} color="inherit" />}
                            {refreshingMarket ? 'Refreshing market data…' : 'Refresh market data'}
                            {' '}
                            <ArrowForward />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {marketRefreshRequest?.zipCode === area.zipCode && marketRefreshMessage && (
                    <p className={`ah-market-refresh-status${marketRefreshError ? ' ah-market-error' : ''}`} role={marketRefreshError ? 'alert' : 'status'}>
                      {marketRefreshMessage}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="ah-empty">
                <TrendingUp />
                <h3>Put your farm on the map.</h3>
                <p>Your campaign properties will bring local ZIP insights here.</p>
                <Link className="ah-text-button" to="/dashboard/campaigns">
                  Explore your campaigns
                  <ArrowForward />
                </Link>
              </div>
            )}
          </section>
          {!noTouches && (
            <section className="ah-panel ah-momentum" aria-labelledby="ah-momentum-title" data-tour-id="dashboard-engagement-panel">
              <div className="ah-section-head">
                <div>
                  <h2 id="ah-momentum-title" data-tour-id="dashboard-engagement">
                    {recent.engagementEvents
                      ? 'Your outreach is getting a response.'
                      : 'Your next response starts with showing up.'}
                  </h2>
                </div>
              </div>
              <div className="ah-chart-panel">
                <div className="ah-chart-summary">
                  <div className="ah-headline-number">
                    {number(recent.engagedProspects)}
                    {' '}
                    <span>prospects engaged over the last 90 days</span>
                  </div>
                </div>
                <EngagementChart performance={recent} />
              </div>
              <div className="ah-channels">
                <div className="ah-channel-label">HOW YOUR BRAND SHOWS UP · LIFETIME</div>
                {[
                  { label: 'Emails sent', types: ['EMAIL'] },
                  { label: 'Mail pieces sent', types: ['POSTCARD', 'LETTER'] },
                  { label: 'Voicemails sent', types: ['VOICEMAIL'] },
                  ...(channelCount(['TEXT']) ? [{ label: 'Text messages sent', types: ['TEXT'] }] : []),
                ].map((channel) => (
                  <div className="ah-channel" data-channel={channel.types[0]} key={channel.label}>
                    {channel.types[0] === 'VOICEMAIL' ? <PhoneOutlined /> : <MailOutline />}
                    <div>
                      {channel.label}
                      <div className="ah-channel-bar">
                        <i
                          style={{
                            width: `${Math.min(
                              100,
                              (channelCount(channel.types) / Math.max(1, lifetime.brandedTouches)) * 100,
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="ah-channel-values">
                      <strong>{number(channelCount(channel.types))}</strong>
                      <RecentValue value={channelCount(channel.types, 'last90Days')} />
                    </div>
                  </div>
                ))}
                {recent.inquiries > 0 && (
                  <p className="ah-momentum-note">
                    <strong>
                      {number(recent.inquiries)}
                      {' '}
                      {recent.inquiries === 1 ? 'person took' : 'people took'}
                      {' '}
                      the next
                      step
                    </strong>
                    {' '}
                    by submitting a form or requesting a report in the last 90 days.
                  </p>
                )}
              </div>
            </section>
          )}
          <section className="ah-upcoming" aria-labelledby="ah-upcoming-title">
            <div className="ah-section-head">
              <div>
                <h2 id="ah-upcoming-title" data-tour-id="dashboard-upcoming">Upcoming touches</h2>
                <p>Your scheduled outreach for the next two weeks.</p>
              </div>
            </div>
            {data?.upcomingTouches?.length ? (
              <ul className="ah-upcoming-list">
                {data.upcomingTouches.map((touch) => {
                  const isMailer = touch.type === 'POSTCARD' || touch.type === 'LETTER';
                  return (
                    <li key={touch.id}>
                      <Link className="ah-upcoming-touch" to={`/dashboard/campaigns/${touch.campaign.id}`}>
                        <time className="ah-upcoming-date" dateTime={touch.sendAt} title={date(touch.sendAt, true)} aria-label={date(touch.sendAt, true)}>
                          <span>{new Date(touch.sendAt).toLocaleDateString('en-US', { month: 'short' })}</span>
                          <strong>{new Date(touch.sendAt).getDate()}</strong>
                        </time>
                        <div className="ah-upcoming-copy">
                          <h3>{touch.campaign.name}</h3>
                          <span className="ah-upcoming-type">
                            {touch.isIntroEmail ? 'Introductory Email' : `${touch.isExtra ? 'Extra ' : ''}${touch.type ? touchLabels[touch.type] : 'Touch'}${!touch.isExtra && touch.sequenceNumber != null ? ` #${touch.sequenceNumber}` : ''}`}
                          </span>
                          {isMailer && touch.processAt && (
                            <span className="ah-upcoming-schedule">
                              Mailing out
                              {' '}
                              <time dateTime={touch.processAt}>
                                {mailingDate(touch.processAt)}
                              </time>
                            </span>
                          )}
                        </div>
                        <span className="ah-sr-only">View campaign</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="ah-empty">
                <EventOutlined aria-hidden="true" />
                <h3>No touches scheduled in the next two weeks.</h3>
                <p>Your upcoming campaign touches will appear here.</p>
              </div>
            )}
          </section>
        </>
      )}
      {dialog !== null && (
        <ActionModal
          title={dialog === 'activity' ? actionHeading(action?.description || action?.title || 'Review follow-up') : 'How the estimate adds up'}
          titleSize="large"
          onClose={closeDialog}
          disableClose={saving}
          fullWidth
          scroll="paper"
          maxWidth={dialog === 'activity' ? 'md' : 'sm'}
          backgroundColor="#f7f8f6"
          textColor={theme.palette.darkGray.main}
          buttons={[
            <ButtonBase key="close" disabled={saving} onClick={closeDialog} style={{ backgroundColor: theme.palette.grayScale6.main, color: theme.palette.darkGray.main }}>Close</ButtonBase>,
            ...(dialog === 'activity' && action ? [
              <ButtonBase key="save" disabled={saving} onClick={saveAction} className="ah-dialog-primary">
                {saving ? 'Saving…' : action.status === 'done' ? 'Reopen follow-up' : 'Mark done'}
              </ButtonBase>,
            ] : []),
          ]}
          message={(
            <div className="agent-home-dialog ah-dialog-content" data-kind={action?.kind} data-status={action?.status} data-signal={action && isQrAction(action) ? 'qr' : undefined}>
              {dialog === 'activity' && action && (
                <>
                  <section className="ah-contact-summary" aria-label="Prospect contact details">
                    <div className="ah-person">
                      <div className="ah-avatar">{initials(action.person.name)}</div>
                      <div className="ah-person-main">
                        <strong>{action.person.name}</strong>
                        {siteAddress && <span className="ah-contact-site-address">{siteAddress}</span>}
                        {(action.person.phoneNumber || action.person.email) && (
                          <div className="ah-contact-details">
                            {action.person.phoneNumber && (
                              <a href={`tel:${action.person.phoneNumber.replace(/[^+\d]/g, '')}`}>
                                <PhoneOutlined />
                                <span>{action.person.phoneNumber}</span>
                              </a>
                            )}
                            {action.person.email && (
                              <a href={`mailto:${encodeURIComponent(action.person.email)}`}>
                                <MailOutline />
                                <span>{action.person.email}</span>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                  {signalLabel(action) && (
                    <span className="ah-pill">
                      {signalLabel(action)}
                    </span>
                  )}
                  {action.suggestedAction && (
                    <>
                      <h3>Suggested next step</h3>
                      <div className="ah-quote">{action.suggestedAction}</div>
                    </>
                  )}
                  {action.campaign?.name && (
                    <div className="ah-campaign-context">
                      Campaign:
                      {' '}
                      <Link className="ah-campaign-name" to={`/dashboard/campaigns/${action.campaign.id}`} target="_blank" rel="noopener noreferrer">
                        {action.campaign.name}
                      </Link>
                    </div>
                  )}
                  <ol className="ah-timeline">
                    {action.activity.map((entry, index) => (
                      <li key={`${entry.sourceId}-${index}`}>
                        <time dateTime={entry.occurredAt}>{date(entry.occurredAt, true)}</time>
                        <strong>{entry.type === 'EMAIL_OPEN' ? 'Email opened' : entry.label}</strong>
                        {entry.detail && <p>{entry.detail}</p>}
                      </li>
                    ))}
                  </ol>
                  {action.activityCount > action.activity.length && (
                    <p className="ah-dialog-note">
                      {`Showing ${action.activity.length} cached activities supporting this follow-up.`}
                    </p>
                  )}
                  {saveError && (
                    <p className="ah-error" role="alert">
                      We couldn’t save this follow-up. Your status hasn’t changed. Please try again.
                    </p>
                  )}
                </>
              )}
              {dialog === 'calculation' && area && opportunity != null && (
                <div className="ah-calculation">
                  <div className="ah-calculation-result">
                    <span>{`Estimated annual commission pool · ZIP ${area.zipCode}`}</span>
                    <strong>{money(opportunity)}</strong>
                  </div>
                  <h3>Multiply these four figures</h3>
                  <dl className="ah-calculation-breakdown">
                    {[
                      ['Properties in this ZIP', number(area.metrics?.numberOfHomes)],
                      ['Annual turnover', `${number(area.metrics?.turnoverRate)}%`],
                      ['Average sale price', money(area.metrics?.averageSalesPrice)],
                      ['Assumed commission rate', '3%'],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="ah-calculation-note">
                    Based on the last 12 months of sales across the full ZIP. One transaction side, before splits,
                    expenses and taxes. This is market opportunity, not projected earnings.
                  </p>
                </div>
              )}
            </div>
          )}
        />
      )}
    </main>
  );
};

export default AgentHomePage;
